import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api, inr, imageUrl } from "@/lib/api";
import { CheckCircle2, Circle, Download, PartyPopper, Sparkles, Package, Truck, Home, ShieldCheck, Clock, Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STEPS = [
  { key: "awaiting",   label: "Awaiting",   icon: Clock },
  { key: "confirmed",  label: "Confirmed",  icon: Sparkles },
  { key: "packed",     label: "Packed",     icon: Package },
  { key: "dispatched", label: "Dispatched", icon: Truck },
  { key: "delivered",  label: "Delivered",  icon: Home },
];

const STATUS_META = {
  awaiting:   { color: "yellow",  message: "Awaiting confirmation from our team" },
  confirmed:  { color: "blue",    message: "Order confirmed by our team" },
  packed:     { color: "blue",    message: "Your crackers are safely packed" },
  dispatched: { color: "indigo",  message: "On its way to your doorstep" },
  delivered:  { color: "emerald", message: "Delivered — enjoy responsibly!" },
  cancelled:  { color: "red",     message: "This order was cancelled" },
};

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fresh confirmation animation only when arriving right after checkout
  const [showConfetti, setShowConfetti] = useState(location.state?.fromCheckout || location.search.includes("just_placed"));

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => {
    if (!showConfetti) return;
    const t = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(t);
  }, [showConfetti]);

  if (loading) {
    return <div className="py-20 text-center text-hyd-text2">Loading order details…</div>;
  }
  if (!order) {
    return <div className="py-20 text-center text-hyd-text2">Order not found.</div>;
  }

  const currentIdx = STEPS.findIndex((s) => s.key === order.status);
  const meta = STATUS_META[order.status] || STATUS_META.awaiting;
  const isFresh = order.status === "awaiting";

  const copyOrderNumber = () => {
    navigator.clipboard?.writeText(order.order_number);
    toast.success("Order number copied");
  };

  const downloadInvoice = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>Invoice ${order.order_number}</title>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>
        body{font-family:'Montserrat',sans-serif;padding:24px;color:#111;max-width:720px;margin:auto}
        header{border-bottom:2px solid #E53935;padding-bottom:12px;margin-bottom:20px}
        h1{color:#E53935;font-family:'Montserrat',sans-serif;margin:0 0 4px}
        .muted{color:#666;font-size:12px}
        table{width:100%;border-collapse:collapse;margin-top:12px;font-size:14px}
        th,td{padding:8px;border-bottom:1px solid #eee;text-align:left}
        th{background:#111;color:#FFD700}
        .r{text-align:right}
        .totals td{padding:4px 8px;border:0}
        .grand{font-size:18px;color:#111;border-top:2px solid #E53935;padding-top:8px}
      </style></head><body>
      <header>
        <h1>Hyderabad Crackers</h1>
        <div class="muted">Tax Invoice · ${order.order_number}</div>
        <div class="muted">Date: ${new Date(order.created_at).toLocaleString()}</div>
      </header>
      <div><b>Bill to:</b> ${order.user_name || ""} · ${order.user_phone}</div>
      <div class="muted">Ship: ${order.address.name}, ${order.address.line1}${order.address.line2 ? ", " + order.address.line2 : ""}, ${order.address.city}, ${order.address.state} - ${order.address.pincode}</div>
      <table>
        <thead><tr><th>Item</th><th>Qty</th><th class="r">Rate</th><th class="r">Amount</th></tr></thead>
        <tbody>
          ${order.items.map((i) => `<tr><td>${i.name}</td><td>${i.qty}</td><td class="r">₹${i.unit_price}</td><td class="r">₹${(i.qty * i.unit_price).toFixed(2)}</td></tr>`).join("")}
        </tbody>
      </table>
      <table class="totals" style="margin-top:12px">
        <tr><td class="r">Subtotal</td><td class="r" style="width:120px">₹${order.subtotal}</td></tr>
        <tr><td class="r">Delivery</td><td class="r">₹${order.delivery_fee}</td></tr>
        ${order.discount > 0 ? `<tr><td class="r">Coupon (${order.coupon_code || ""})</td><td class="r">- ₹${order.discount}</td></tr>` : ""}
        <tr class="grand"><td class="r"><b>TOTAL</b></td><td class="r"><b>₹${order.total}</b></td></tr>
      </table>
      <p style="margin-top:24px;font-size:12px;color:#555">Handle with care. Fireworks are age-restricted (18+). Follow all safety instructions on the pack. Do not open near open flame.</p>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Fresh confirmation banner */}
      {isFresh && (
        <div data-testid="order-success-banner" className="relative overflow-hidden rounded-3xl border border-hyd-gold/40 bg-gradient-to-br from-hyd-red/20 via-hyd-bg to-hyd-gold/10 p-6 sm:p-8 grain-overlay">
          {showConfetti && <ConfettiBurst />}
          <div className="relative flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-hyd-gold grid place-items-center shrink-0 shadow-glow-gold animate-pop">
              <PartyPopper className="w-7 h-7 text-hyd-bg" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-hyd-gold font-bold">Order received</div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">
                Thanks{order.user_name ? `, ${order.user_name.split(" ")[0]}` : ""}!
              </h1>
              <p className="text-hyd-text2 text-sm mt-1 max-w-lg">
                Your order has been initiated. Cash on delivery — please keep <span className="text-hyd-gold font-bold">{inr(order.total)}</span> ready.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={copyOrderNumber} className="bg-hyd-panel border border-hyd-border text-hyd-gold rounded-full px-3 py-1.5 text-xs font-mono font-bold flex items-center gap-2 hover:border-hyd-gold">
                  {order.order_number} <Copy className="w-3 h-3" />
                </button>
                <Button size="sm" onClick={downloadInvoice} variant="secondary" className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim rounded-full h-8">
                  <Download className="w-3.5 h-3.5 mr-1" /> Invoice
                </Button>
              </div>
            </div>
          </div>

          {/* Awaiting highlight banner */}
          <div data-testid="order-awaiting-banner" className="relative mt-5 rounded-2xl border-2 border-hyd-gold/70 bg-hyd-gold/10 p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-hyd-gold shrink-0 mt-0.5 animate-pulse" />
            <div className="text-sm">
              <div className="font-display text-lg font-bold text-hyd-gold">Thanks for initiating the order</div>
              <p className="text-hyd-text mt-1">
                Our executive will <span className="font-bold text-hyd-gold">call you shortly to confirm the order</span>, and your crackers will be delivered in <span className="font-bold text-hyd-gold">24–48 hours</span>.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isFresh && (
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <Link to="/orders" className="text-xs uppercase text-hyd-text2 hover:text-hyd-gold">← All orders</Link>
          <div>
            <h1 className="font-display text-2xl font-extrabold">Order {order.order_number}</h1>
            <div className="text-xs text-hyd-text3">{new Date(order.created_at).toLocaleString()}</div>
          </div>
          <Button onClick={downloadInvoice} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-gold rounded-full">
            <Download className="w-4 h-4 mr-1" /> Tax Invoice
          </Button>
        </div>
      )}

      {/* Status stepper */}
      {order.status !== "cancelled" ? (
        <div className="mt-5 bg-hyd-panel border border-hyd-border rounded-3xl p-5">
          <div className="text-xs uppercase tracking-widest text-hyd-text2 mb-1">Status</div>
          <div className="text-hyd-gold font-display text-xl font-bold mb-4">{meta.message}</div>
          <div className="grid grid-cols-5 gap-2 relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} className="flex flex-col items-center gap-2 text-center relative">
                  <div className={`w-11 h-11 rounded-full grid place-items-center border-2 transition-colors ${
                    done ? "bg-hyd-gold border-hyd-gold text-hyd-bg" : "bg-hyd-bg border-hyd-border text-hyd-text3"
                  } ${active ? "shadow-glow-gold" : ""}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className={`text-[10px] uppercase font-bold tracking-widest ${done ? "text-hyd-gold" : "text-hyd-text3"}`}>{s.label}</div>
                  {i < STEPS.length - 1 && (
                    <div className={`absolute top-5 left-[calc(50%+22px)] right-[calc(-50%+22px)] h-0.5 ${i < currentIdx ? "bg-hyd-gold" : "bg-hyd-border"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-5 p-4 rounded-2xl bg-hyd-red/10 border border-hyd-red/30 text-hyd-red">
          This order was cancelled.
        </div>
      )}

      {/* Two-column layout */}
      <div className="mt-5 grid md:grid-cols-3 gap-5">
        {/* Items */}
        <div className="md:col-span-2 bg-hyd-panel border border-hyd-border rounded-3xl overflow-hidden">
          <div className="px-4 py-3 border-b border-hyd-border flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-hyd-text2">
              {order.items.length} item{order.items.length > 1 ? "s" : ""}
            </div>
            <Link to="/orders" className="text-xs uppercase text-hyd-gold hover:underline">All orders</Link>
          </div>
          <div className="divide-y divide-hyd-border">
            {order.items.map((i, idx) => (
              <div key={idx} className="p-4 flex gap-3 items-center">
                <div className="w-14 h-14 rounded-2xl bg-hyd-panel2 overflow-hidden shrink-0 border border-hyd-border">
                  {i.image_url ? <img src={imageUrl(i.image_url)} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold line-clamp-2">{i.name}</div>
                  <div className="text-xs text-hyd-text2 mt-0.5">{i.qty} × {inr(i.unit_price)}</div>
                </div>
                <div className="font-bold text-hyd-gold text-sm shrink-0">{inr(i.qty * i.unit_price)}</div>
              </div>
            ))}
          </div>
          {isFresh && (
            <div className="p-4 border-t border-hyd-border bg-hyd-bg/40 flex items-start gap-2 text-xs text-hyd-text2">
              <ShieldCheck className="w-4 h-4 text-hyd-gold shrink-0 mt-0.5" />
              <span>Please handle with care. Read the safety instructions inside each pack. Keep out of reach of children under 12.</span>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-hyd-panel border border-hyd-border rounded-3xl p-4">
            <div className="text-xs uppercase tracking-widest text-hyd-text2 mb-2">Shipping to</div>
            <div className="text-sm text-hyd-text font-semibold">{order.address.name}</div>
            <div className="text-sm text-hyd-text2">{order.address.line1}{order.address.line2 ? ", " + order.address.line2 : ""}</div>
            <div className="text-sm text-hyd-text2">{order.address.city}, {order.address.state} - {order.address.pincode}</div>
            <div className="text-sm text-hyd-text2 mt-1">📞 {order.address.phone}</div>
            {order.delivery_slot && (
              <div className="mt-3 flex items-center gap-2 text-xs bg-hyd-bg border border-hyd-gold/40 rounded-2xl px-3 py-2 text-hyd-gold">
                <Clock className="w-4 h-4" />
                <span className="font-medium">{order.delivery_slot.replace("|", " · ")}</span>
              </div>
            )}
          </div>

          <div className="bg-hyd-panel border border-hyd-border rounded-3xl p-4">
            <div className="text-xs uppercase tracking-widest text-hyd-text2 mb-2">Payment</div>
            <Row label="Subtotal" value={inr(order.subtotal)} />
            <Row label="Delivery" value={order.delivery_fee === 0 ? "FREE" : inr(order.delivery_fee)} valueClass={order.delivery_fee === 0 ? "text-emerald-400" : ""} />
            {order.discount > 0 && (
              <Row label={`Coupon (${order.coupon_code || "applied"})`} value={`- ${inr(order.discount)}`} valueClass="text-emerald-400" />
            )}
            <div className="border-t border-hyd-border my-2" />
            <Row label="Total" value={inr(order.total)} bold />
            <div className="mt-3 text-xs bg-hyd-bg border border-hyd-border rounded-2xl px-3 py-2 text-hyd-text2 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-hyd-gold shrink-0 mt-0.5" />
              <div>
                <b className="text-hyd-text">Cash on Delivery</b> — please keep {inr(order.total)} ready when your order arrives.
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={() => navigate("/")} className="flex-1 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim rounded-full font-bold">
              Continue shopping
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueClass = "", bold = false }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-hyd-text2">{label}</span>
      <span className={valueClass || "text-hyd-text"}>{value}</span>
    </div>
  );
}

function ConfettiBurst() {
  const pieces = Array.from({ length: 24 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 400;
        const duration = 1400 + Math.random() * 1200;
        const rotate = Math.random() * 360;
        const colors = ["#FFD700", "#E53935", "#F0C800", "#FFA000"];
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            style={{
              left: `${left}%`,
              top: "-10px",
              background: color,
              transform: `rotate(${rotate}deg)`,
              animation: `confetti-fall ${duration}ms ${delay}ms linear forwards`,
            }}
            className="absolute w-2 h-3 rounded-sm opacity-90"
          />
        );
      })}
      <style>{`@keyframes confetti-fall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1 } 100% { transform: translateY(500px) rotate(720deg); opacity: 0 } }`}</style>
    </div>
  );
}
